import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetAgenticRunsEnvironmentsQuery,
  useGetAgenticRunsQuery,
  useLazyGetAgenticRunsQuery,
  selectSelectedControlTowerEnv,
  setSelectedControlTowerEnv,
} from "../../../app/api/console/agenticRunsApiSlice";
import {
  RUN_STATUS_CONFIG,
  outcomeCfg,
  formatUsd,
  formatDuration,
  timeAgo,
  displayRunStatus,
} from "./outcomeConfig";

const POLL_ACTIVE_MS = 5000;
const POLL_IDLE_MS = 30000;

function RunStatusBadge({ status }) {
  const cfg = RUN_STATUS_CONFIG[status] || {
    label: status || "?",
    bg: "bg-gray-100",
    text: "text-gray-500",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.pulse && <span className='w-2 h-2 rounded-full bg-blue-500 animate-pulse' />}
      {cfg.label}
    </span>
  );
}

export function OutcomeChips({ counts, max = 4 }) {
  const entries = Object.entries(counts || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => {
      // Failures first (that's the diagnostic signal), then by count.
      const fa = outcomeCfg(a[0]).failure ? 0 : 1;
      const fb = outcomeCfg(b[0]).failure ? 0 : 1;
      return fa - fb || b[1] - a[1];
    });
  if (!entries.length) return <span className='text-xs text-gray-400'>—</span>;
  const shown = entries.slice(0, max);
  const rest = entries.slice(max);
  return (
    <div className='flex flex-wrap gap-1'>
      {shown.map(([cls, count]) => {
        const cfg = outcomeCfg(cls);
        return (
          <span
            key={cls}
            title={cls}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>
            {count} {cfg.label}
          </span>
        );
      })}
      {rest.length > 0 && (
        <span
          title={rest.map(([c, n]) => `${n} ${outcomeCfg(c).label}`).join(", ")}
          className='inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] text-gray-500 bg-gray-50 border border-gray-200'>
          +{rest.length}
        </span>
      )}
    </div>
  );
}

function WorkersBar({ ok, total }) {
  if (!total) return <span className='text-xs text-gray-400'>—</span>;
  const pct = Math.round((ok / total) * 100);
  return (
    <div className='flex items-center gap-2 min-w-[90px]'>
      <div className='flex-1 h-1.5 rounded-full bg-red-200 overflow-hidden'>
        <div className='h-full bg-green-500' style={{ width: `${pct}%` }} />
      </div>
      <span className='text-[11px] text-gray-600 whitespace-nowrap'>
        {ok}/{total}
      </span>
    </div>
  );
}

function ControlTower() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedEnv = useSelector(selectSelectedControlTowerEnv);

  const { data: envData } = useGetAgenticRunsEnvironmentsQuery();
  const environments = useMemo(() => envData?.environments || [], [envData]);

  const env = useMemo(() => {
    if (savedEnv && environments.some((e) => e.env === savedEnv)) return savedEnv;
    if (environments.some((e) => e.env === "staging")) return "staging";
    return environments[0]?.env || null;
  }, [savedEnv, environments]);

  const [statusFilter, setStatusFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");
  const [pollMs, setPollMs] = useState(POLL_IDLE_MS);
  // Older pages fetched via the backend's next_cursor ("Load more"). Page 1
  // keeps polling; extra pages are immutable snapshots appended below it.
  const [extraPages, setExtraPages] = useState([]);

  const {
    data,
    isFetching,
    isLoading,
    error,
    refetch,
  } = useGetAgenticRunsQuery(
    { env, status: statusFilter || undefined, limit: 100 },
    { skip: !env, pollingInterval: pollMs }
  );

  const [loadMoreRuns, { isFetching: loadingMore, error: loadMoreError }] =
    useLazyGetAgenticRunsQuery();

  // A new env/filter window invalidates cursors from the previous one.
  useEffect(() => {
    setExtraPages([]);
  }, [env, statusFilter]);

  const nextCursor = extraPages.length
    ? extraPages[extraPages.length - 1]?.next_cursor || null
    : data?.next_cursor || null;

  const runs = useMemo(() => {
    const pageOne = data?.runs || [];
    // Dedupe by run_id: a run whose status flips between two page fetches
    // moves DynamoDB partitions and can reappear in a later page.
    const seen = new Set();
    let list = [];
    for (const r of [...pageOne, ...extraPages.flatMap((p) => p?.runs || [])]) {
      if (seen.has(r.run_id)) continue;
      seen.add(r.run_id);
      list.push(r);
    }
    if (textFilter.trim()) {
      const q = textFilter.trim().toLowerCase();
      list = list.filter((r) =>
        [r.skill_label, r.website_name, r.website_id, r.agent_id, r.caller_id, r.run_id]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    return list;
  }, [data, extraPages, textFilter]);

  // Poll fast while anything is live, slow otherwise.
  useEffect(() => {
    const anyRunning = (data?.runs || []).some((r) => r.status === "running");
    setPollMs(anyRunning ? POLL_ACTIVE_MS : POLL_IDLE_MS);
  }, [data]);

  const canRefresh = Boolean(env);
  const handleRefresh = () => {
    if (canRefresh) refetch();
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    try {
      const page = await loadMoreRuns({
        env,
        status: statusFilter || undefined,
        limit: 100,
        cursor: nextCursor,
      }).unwrap();
      setExtraPages((prev) => [...prev, page]);
    } catch {
      // Surfaced via loadMoreError next to the button; the cursor state is
      // untouched so the operator can simply retry.
    }
  };

  const statuses = ["", "running", "completed", "failed", "orphaned"];

  return (
    <div className='w-full max-w-none'>
      {/* Header */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <h1 className='text-xl font-semibold text-gray-900'>Control Tower</h1>
          <p className='text-sm text-gray-500 mt-1'>
            Live orchestrator runs across all websites — typed worker outcomes
            show whether low issue counts come from the skill or from infra
            (Cloudflare, MCP timeouts…).
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <select
            value={env || ""}
            onChange={(e) => dispatch(setSelectedControlTowerEnv(e.target.value))}
            className='text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500'>
            {environments.map((e) => (
              <option key={e.env} value={e.env}>
                {e.env}
              </option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            disabled={!canRefresh}
            className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'>
            <svg
              className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              strokeWidth={2}>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap items-center gap-2 mb-4'>
        <div className='flex rounded-lg border border-gray-200 bg-white p-0.5'>
          {statuses.map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                statusFilter === s
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600 hover:bg-gray-50"
              }`}>
              {s ? RUN_STATUS_CONFIG[s]?.label || s : "All"}
            </button>
          ))}
        </div>
        <input
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          placeholder='Filter by site, agent, launcher, run id…'
          className='flex-1 min-w-[220px] text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500'
        />
        <span className='text-xs text-gray-400'>
          {runs.length} run{runs.length === 1 ? "" : "s"}
          {nextCursor ? " · more available" : ""}
          {data?.index_used === false && " · index backfilling (scan fallback)"}
        </span>
      </div>

      {error && (
        <div className='mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700'>
          Failed to load runs: {error?.data?.detail || error?.error || "unknown error"}
        </div>
      )}

      {/* Table */}
      <div className='bg-white border border-gray-200 rounded-xl overflow-hidden'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200'>
              <th className='px-4 py-2.5 font-semibold'>Status</th>
              <th className='px-4 py-2.5 font-semibold'>Agent</th>
              <th className='px-4 py-2.5 font-semibold'>Website</th>
              <th className='px-4 py-2.5 font-semibold'>Launched by</th>
              <th className='px-4 py-2.5 font-semibold'>Started</th>
              <th className='px-4 py-2.5 font-semibold'>Duration</th>
              <th className='px-4 py-2.5 font-semibold'>Cost</th>
              <th className='px-4 py-2.5 font-semibold'>Workers</th>
              <th className='px-4 py-2.5 font-semibold'>Outcomes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className='px-4 py-10 text-center text-gray-400'>
                  Loading runs…
                </td>
              </tr>
            ) : runs.length === 0 ? (
              <tr>
                <td colSpan={9} className='px-4 py-10 text-center text-gray-400'>
                  No runs found{statusFilter ? ` with status "${statusFilter}"` : ""}.
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <tr
                  key={r.run_id}
                  onClick={() => navigate(`/admin/control-tower/${env}/${r.run_id}`)}
                  className='border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors'>
                  <td className='px-4 py-2.5'>
                    <RunStatusBadge status={displayRunStatus(r)} />
                    {r.error && !r.stopped && (
                      <div
                        className='text-[11px] text-red-600 mt-0.5 max-w-[160px] truncate'
                        title={r.error}>
                        {r.error}
                      </div>
                    )}
                    {r.stopped && (
                      <div className='text-[11px] text-amber-700 mt-0.5 max-w-[160px] truncate'>
                        User stopped
                      </div>
                    )}
                  </td>
                  <td className='px-4 py-2.5'>
                    <div className='font-medium text-gray-800'>
                      {r.skill_label || r.agent_id || "—"}
                    </div>
                    {r.skill_label && (
                      <div className='text-[11px] text-gray-400'>{r.agent_id}</div>
                    )}
                  </td>
                  <td className='px-4 py-2.5'>
                    <div className='text-gray-800'>
                      {r.website_name || r.website_id || "—"}
                    </div>
                    {r.website_name && (
                      <div className='text-[11px] text-gray-400'>{r.website_id}</div>
                    )}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600'>{r.caller_id || "—"}</td>
                  <td className='px-4 py-2.5 text-gray-600' title={r.started_at}>
                    {timeAgo(r.started_at)}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600'>
                    {formatDuration(r.duration_seconds)}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600'>
                    {formatUsd(r.spent_usd)}
                    {r.budget_max_usd > 0 && (
                      <span className='text-[11px] text-gray-400'>
                        {" "}
                        / {formatUsd(r.budget_max_usd)}
                      </span>
                    )}
                  </td>
                  <td className='px-4 py-2.5'>
                    <WorkersBar ok={r.workers_ok} total={r.workers_total} />
                  </td>
                  <td className='px-4 py-2.5'>
                    <OutcomeChips counts={r.outcome_counts} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination — older runs beyond the live top-100 window */}
      {(nextCursor || extraPages.length > 0) && (
        <div className='flex flex-col items-center gap-1.5 mt-4'>
          {nextCursor ? (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className='px-4 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'>
              {loadingMore ? "Loading…" : "Load more runs"}
            </button>
          ) : (
            <span className='text-xs text-gray-400'>
              You've reached the oldest runs.
            </span>
          )}
          {loadMoreError && (
            <span className='text-xs text-red-600'>
              Failed to load more runs:{" "}
              {loadMoreError?.data?.detail || loadMoreError?.error || "unknown error"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default ControlTower;
